import React, { useEffect } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { RelationalOperatorKey } from '../../../../../utils/apollo/graphqlSchema';
import { IFilterPredicate, IPredicateFormFieldPath } from '../../../types';
import * as FilterWidgetPredicateUI from './FilterWidgetPredicateUI';
import MatchCaseToggle from './MatchCaseToggle';
import OperandTypeToggle from './OperandTypeToggle';
import PredicateOperator from './PredicateOperator';
import PredicateOperands from './PredicateOperands';
import useFilterWidgetFormContext from '../../../utils/useFilterWidgetFormContext';
import PredicateField from './PredicateField';
import { useFilterWidgetPropContext } from '../../../FilterWidgetPropContext';
import { getPredicateFormFieldPath } from '../../../utils/formPath';
import { SquareRemoveButton } from '../../../../Button';

interface IProps {
  formPath: IPredicateFormFieldPath;
  onResetField: () => void;
  onRemove: () => void;
  disableRemove: boolean;
  rootPredicate: IFilterPredicate;
  onUpdateRootPredicate: (newPredicate: IFilterPredicate) => void;
}

const FilterWidgetSimplePredicate = ({
  formPath,
  onResetField,
  onRemove,
  disableRemove,
  rootPredicate,
  onUpdateRootPredicate,
}: IProps) => {
  const { lockedField, includeAnyValue } = useFilterWidgetPropContext();
  const { control, setFocus } = useFilterWidgetFormContext();

  const fieldPath = getPredicateFormFieldPath(formPath);

  const selectedRelOp = useWatch({
    name: `${fieldPath}.relOp`,
    control,
  });

  const hasSingleValue = selectedRelOp?.key !== RelationalOperatorKey.Between;
  const supportCaseInsensitive = selectedRelOp?.supportCaseInsensitive ?? false;

  useEffect(() => {
    if (lockedField && !selectedRelOp) {
      setFocus(`${fieldPath}.relOp`);
    }
  }, [fieldPath, lockedField, selectedRelOp, setFocus]);

  return (
    <FilterWidgetPredicateUI.Container>
      <div>
        <PredicateField
          formPath={formPath}
          onReset={onResetField}
          rootPredicate={rootPredicate}
          onUpdateRootPredicate={onUpdateRootPredicate}
        />
      </div>
      <div>
        <PredicateOperator formPath={formPath} />
      </div>
      <FilterWidgetPredicateUI.ValueWrapper
        $hasSingleValue={hasSingleValue}
        $supportCaseInsensitive={supportCaseInsensitive}
      >
        <PredicateOperands formPath={formPath} />
        {supportCaseInsensitive && (
          <Controller
            name={`${fieldPath}.matchCase`}
            control={control}
            render={({ field: { onChange, value } }) => (
              <MatchCaseToggle value={value} onChange={onChange} />
            )}
          />
        )}
      </FilterWidgetPredicateUI.ValueWrapper>
      <FilterWidgetPredicateUI.ActionsWrapper>
        <OperandTypeToggle formPath={formPath} />
        {!disableRemove && !includeAnyValue && (
          <SquareRemoveButton onClick={onRemove} content={'Remove Filter'} />
        )}
      </FilterWidgetPredicateUI.ActionsWrapper>
    </FilterWidgetPredicateUI.Container>
  );
};

export default FilterWidgetSimplePredicate;
